import { useMemo } from "react";
import { Square } from "lucide-react";
import useTableState from "@/hooks/useTableState";
import { TableToolbar, Th, filterSelectCls, matchSearch, sortRows } from "@/components/DataTable";

const STATUS_CLS = {
  success: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
  running: "border-sky-500/50 bg-sky-500/10 text-sky-400",
  failed: "border-red-500/50 bg-red-500/10 text-red-400",
  quota_exhausted: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  stopped: "border-violet-500/50 bg-violet-500/10 text-violet-400",
};

export default function JobLogsTable({ logs, t, onStop }) {
  const ts = useTableState("j", { sort: "started_at", dir: "desc" });
  const jobs = useMemo(() => [...new Set(logs.map((l) => l.job).filter(Boolean))].sort(), [logs]);
  const statuses = useMemo(() => [...new Set(logs.map((l) => l.status).filter(Boolean))].sort(), [logs]);

  const rows = (() => {
    let r = logs.filter((l) => matchSearch(l, ts.get("q"), ["job", "detail", "league_id"]));
    const st = ts.get("status");
    if (st) r = r.filter((l) => l.status === st);
    const jb = ts.get("job");
    if (jb) r = r.filter((l) => l.job === jb);
    const from = ts.get("from");
    if (from) r = r.filter((l) => (l.started_at || "") >= from);
    return sortRows(r, ts.get("sort"), ts.get("dir"));
  })();

  return (
    <div data-testid="job-logs-table">
      <TableToolbar ts={ts} t={t} placeholder={t("admin.job")}>
        <select data-testid="j-filter-status" className={filterSelectCls} value={ts.get("status")} onChange={(e) => ts.set("status", e.target.value)}>
          <option value="">{t("admin.status")} — {t("filters.all")}</option>
          {statuses.map((s) => <option key={s} value={s}>{t(`admin.${s}`)}</option>)}
        </select>
        <select data-testid="j-filter-job" className={filterSelectCls} value={ts.get("job")} onChange={(e) => ts.set("job", e.target.value)}>
          <option value="">{t("admin.job")} — {t("filters.all")}</option>
          {jobs.map((j) => <option key={j} value={j}>{t(`admin.${j}`)}</option>)}
        </select>
        <input type="date" data-testid="j-filter-from" className={filterSelectCls} value={ts.get("from")} onChange={(e) => ts.set("from", e.target.value)} />
      </TableToolbar>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <Th k="job" label={t("admin.job")} ts={ts} />
              <Th k="status" label={t("admin.status")} ts={ts} />
              <Th k="started_at" label={t("admin.started")} ts={ts} />
              <Th k="finished_at" label={t("admin.finished")} ts={ts} />
              <Th k="records" label={t("admin.records")} ts={ts} />
              <th className="py-2 pr-3">{t("admin.detail")}</th>
              {onStop && <th className="py-2">{t("admin.stop")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((log) => (
              <tr key={log.id || `${log.job}-${log.started_at}-${log.status}`} className="border-b border-slate-800/60 text-slate-300" data-testid="admin-job-log-row">
                <td className="py-2 pr-3 font-mono2">{log.job}{log.league_id ? ` · ${log.league_id}` : ""}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full border px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[log.status] || ""}`}>
                    {t(`admin.${log.status}`)}
                  </span>
                </td>
                <td className="whitespace-nowrap py-2 pr-3 font-mono2 text-[10px] text-slate-500">{log.started_at ? new Date(log.started_at).toLocaleString() : "—"}</td>
                <td className="whitespace-nowrap py-2 pr-3 font-mono2 text-[10px] text-slate-500">{log.finished_at ? new Date(log.finished_at).toLocaleString() : "—"}</td>
                <td className="py-2 pr-3 font-mono2">{log.records}</td>
                <td className="max-w-xs truncate py-2 pr-3 font-mono2 text-[10px] text-slate-500" title={log.detail || ""}>{log.detail || "—"}</td>
                {onStop && (
                  <td className="py-2">
                    {log.status === "running" && (
                      <button
                        data-testid={`stop-job-${log.id}`}
                        onClick={() => onStop(log)}
                        className="flex items-center gap-1 rounded-md border border-red-500/50 px-2 py-1 font-mono2 text-[10px] font-bold uppercase tracking-wider text-red-400 hover:bg-red-500/10"
                      >
                        <Square className="h-3 w-3" /> {t("admin.stop")}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={onStop ? 7 : 6} className="py-6 text-center text-slate-500">—</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
